<script setup lang="ts">
import { SearchCatalog } from '@gql/SearchCatalog'
import { type BeforeItem, useBreadcums } from './BreadcrumsLine.vue'
import type { RouteLocationRaw } from '#vue-router'

export type FeedSize = 1 | 4 | 6 | 10 | 999
export type Props = {
  options: {
    feedSize?: FeedSize
    sellerId: undefined | (() => string | None)
    isB2b: boolean
    routeName: string
    routeNameWithParams: string
    toProductRoute: (p: Pick<ProductListingBoxFragmentFull, 'slug'>) => RouteLocationRaw | undefined
    breadcrums: () => BeforeItem[] | None
  }
}

export type ErrorMsgStatus = 'FAILD_FETCH' | 'BAD_ARGS' | 'UPDATE_FAILED'
export type FilterActive = { type: 'filter' | 'limit' | 'sku' | 'department' | None; filterKey: None | string } | None
export type InputCatalogType = NN<NN<gen.SearchCatalogQuery['catalogSeller']>['input']>
export type Cursor = InputCatalogType['cursor']
export type NamedFilter = { key: string; name: string }
export type FilterMulti = { key: string; name: string; color: string | None }
export type InputCatalogUi = Replace<
  gen.InputCatalog,
  {
    department?: NamedFilter | None
    sellerCollection?: NamedFilter | None
    limitTo?: { key: gen.CatalogSearchLimitTo; name: string }[] | None
  }
>
export type Updaters = {
  changePage(number: number): NotVoid
  sellerCollectionSer(input: InputCatalogUi['sellerCollection'] | None): void
  limitToSet(input: InputCatalogUi['limitTo'] | None): void
  setFilters(filters: gen.InputCatalog['filters'] | None): void
  setSorting(sort: gen.EnumCatalogSort | None): void
  removeQuery(): void
  setSku(i: string[] | None): void
  setDepartment(i: NamedFilter | None): void
  clearAll(): void
}

export type FilterUpdateProps = Pick<NN<InputCatalogUi['filters']>[0], 'max' | 'min' | 'multi'>

type InputCatalogDynamic = Omit<gen.InputCatalog, 'seller' | 'isB2b'>
type InputCatalogStatic = Pick<InputCatalogUi, 'seller' | 'isB2b'>

type ProductPage = {
  renderSkeletons?: number
  pageNumber: number
  items: gen.ProductListingBoxFragment[]
  last: boolean
}

export type Context = {
  catalog: Omit<NN<gen.SearchCatalogQuery['catalogSeller']>, 'items'> | None
  // items: ProductListingBoxFragmentFull[]
  seller: { sellerId: string | undefined }
  documentId: string | None
  pages: ProductPage[]
  input: gen.InputCatalog | undefined
  totalCount: number
  filtersInput: gen.InputSearchCatalogFiltersSeller
  isEmpty: boolean
}

// sortingPriceAsc: manualTranslations({
//     en: 'Price: Low-High',
//     lt: 'Kaina: Maža-Didelė',
//   }),
//   sortingPriceDsc: manualTranslations({
//     en: 'Price: High-Low',
//     lt: 'Kaina: Didelė-Maža',
//   }),

const CatalogSearchLimitTo: Record<gen.CatalogSearchLimitTo, gen.CatalogSearchLimitTo> = {
  InStock: 'InStock',
  Limited: 'Limited',
  New: 'New',
  Sale: 'Sale',
  Upcoming: 'Upcoming',
}
const EnumCatalogSort: Record<gen.EnumCatalogSort, gen.EnumCatalogSort> = {
  Available: 'Available',
  BestMatch: 'BestMatch',
  EndsAt: 'EndsAt',
  New: 'New',
  PriceAsc: 'PriceAsc',
  PriceDsc: 'PriceDsc',
}

type SearchOptions = { replace?: boolean; delay?: boolean; loadMore?: { nextPage: number } }

const t2 = useT2({
  'No products in category': 'Kategorijoje nėra prekių',
  'No products': 'Nėra prekių',
  never: 'niekada',
  'Incorrect catalog search': 'Neteisinga paieška kataloge',
  'Read more about collection': 'Skaityti daugiau apie kolekciją',
  'Best match': 'Geriausias atitikmuo',
  New: 'Nauja',
  'Price: Low-High': 'Kaina: Maža-Didelė',
  'Price: High-Low': 'Kaina: Didelė-Maža',
  Available: 'Prieinama',
  'Ends at': 'Baigiasi',
  'In stock': 'Yra sandėlyje',
  Sale: 'Išpardavimas',
  Limited: 'Ribotas',
  Upcoming: 'Būsimos prekės',
})

const t3 = computed(() => {
  return {
    i18nSort: {
      BestMatch: t2('Best match'),
      New: t2('New'),
      PriceAsc: t2('Price: Low-High'),
      PriceDsc: t2('Price: High-Low'),
      Available: t2('Available'),
      EndsAt: t2('Ends at'),
    } satisfies Record<gen.EnumCatalogSort, string>,

    limit: {
      New: t2('New'),
      InStock: t2('In stock'),
      // Discounted: 'Discounted',
      // DayDeal: 'Day deal',
      Sale: t2('Sale'),
      Limited: t2('Limited'),
      Upcoming: t2('Upcoming'),
    } satisfies Record<gen.CatalogSearchLimitTo, stringI18n>,
  }
})

const pageSize = computed(() => (mobile.value ? 30 : 60))

function clearnFilters(fitlers: gen.CatalogFiltersInput[] | None) {
  const arr = helpers.arr(fitlers, (i): gen.CatalogFiltersInput | undefined => {
    return !i.key
      ? undefined
      : helpers.isNumber(i.max) || helpers.isNumber(i.min)
        ? { key: i.key, max: i.max || undefined, min: i.min || undefined }
        : i.multi?.length
          ? { key: i.key, multi: helpers.sortSimpleStr(i.multi, (i) => i) }
          : undefined
  })
  return !arr.length
    ? undefined
    : helpers.sortSimpleStr(
        helpers.sortSimpleNum(arr, (i) => i.multi?.length),
        (i) => i.key
      )
}

function toInputCatalog(inp: InputCatalogDynamic | None, staticInp: InputCatalogStatic): gen.InputCatalog {
  return {
    ...staticInp,
    sellerCollection: inp?.sellerCollection || undefined,
    filters: clearnFilters(inp?.filters),
    sku: helpers.arrClean(inp?.sku),
    query: inp?.query || undefined,
    sort: inp?.sort,
    catalogSlug: inp?.catalogSlug || undefined,
    cursor: { number: helpers.toPositive(inp?.cursor?.number, 1), size: helpers.toPositive(inp?.cursor?.size, pageSize.value) },
    department: inp?.department || undefined,
    limitTo: helpers.arrClean(helpers.sortSimpleStr(inp?.limitTo, (i) => i)),
  } satisfies gen.InputCatalog
}

function toUi(inp: { catalog: gen.SearchCatalogQuery['catalogSeller'] | None; sellerId: string | None; options: SearchOptions; prev: Context | None }): {
  inputFast: InputCatalogUi
  context: Context
} {
  const catalog = inp.catalog
  const input = catalog?.input || undefined
  const query = input?.query || undefined
  const sellerId = inp.sellerId || undefined
  const i18nLimitTo = t3.value.limit

  const inputFast: InputCatalogUi = {
    seller: { sellerId },
    query,
    isB2b: !!input?.isB2b,
    filters: clearnFilters(input?.filters),
    sort: input?.sort,
    catalogSlug: input?.catalogSlug || undefined,
    cursor: input?.cursor,
    // priceGroups: input?.priceGroups,
    sku: input?.sku,
    sellerCollection: !input?.sellerCollection ? undefined : { key: input?.sellerCollection, name: catalog?.title || input?.sellerCollection },
    // visibility: input?.visibility,
    department: input?.department ? { key: input?.department, name: catalog?.departmentName || input?.department } : undefined,
    limitTo: helpers.arrClean(helpers.arr(input?.limitTo, (key) => ({ key, name: ts(i18nLimitTo[key]) }))),
  } satisfies InputCatalogUi

  const pages: Context['pages'] = []
  if (inp.options.loadMore) inp.prev?.pages.forEach((i) => pages.push({ ...i, last: false }))
  pages.push({ pageNumber: catalog?.pageInfo?.currentPage ?? 1, items: helpers.arr(catalog?.items), last: true })

  return {
    inputFast,
    context: {
      isEmpty: !pages.length || pages.every((i) => !i.items.length),
      catalog,
      input,
      seller: { sellerId },
      documentId: catalog?.document?._id,
      pages,
      totalCount: helpers.toPositive(catalog?.pageInfo?.totalCount, 0),
      filtersInput: {
        filterKey: undefined,
        // visibility: undefined,
        seller: { sellerId },
        sellerCollection: inputFast.sellerCollection?.key,
        limitTo: inputFast.limitTo?.map((i) => i.key),
        catalogSlug: inputFast.catalogSlug,
        department: inputFast?.department?.key,
        query: inputFast?.query,
        filters: inputFast?.filters,
        isB2b: inputFast.isB2b,
      },
    },
  }
}

const { myLink } = useLinks()
const mobile = useMobile()

const apollo = useApollo('get')
const router = useRouter()
const route = useRoute()

const init1 = useInited('init1')
const init3 = useInited('init3')
const initAds = useInited('initAds')

const { start } = useAsyncStop('catalog:search')
const props = defineProps<Props>()
const feedSize = useState('catalog:feedSize', (): FeedSize => props.options.feedSize ?? 4)

export type FiltersType = 'filters' | 'sort' | 'none'

const toData = () => {
  const { context, inputFast } = toUi({ options: {}, catalog: null, prev: undefined, sellerId: props.options.sellerId?.() || undefined })
  return shallowReactive({
    prevSlug: undefined as string | undefined,
    readDescription: false,
    logError: undefined as undefined | ErrorMsgStatus,
    loadingLoadMoreSkeletonsPage: undefined as undefined | { nextPage: number; size: number },
    loading: false,
    departmentImage: undefined as None | gen.ImageFragment,
    inputFast,
    context,
    fetchState: 'first' as 'first' | 'other' | 'stop',
    filters: { filter: undefined, type: 'none' } as { filter: FilterActive | None; type: FiltersType },
  })
}
const data = toData()

// ---------------- SSR ----------------

function createServerPromise() {
  let resolve: (() => void) | undefined
  let reject: (() => void) | undefined
  if (import.meta.server) {
    const promise = new Promise<void>((reso, rej) => {
      resolve = () => reso()
      reject = () => rej()
    })
    onServerPrefetch(() => promise)
  }
  return { resolve, reject }
}
function useHydrationComponent<T>(key: string, get: () => T, set: (i: T) => void) {
  const nuxtApp = useNuxtApp()

  if (import.meta.server) {
    nuxtApp.hooks.hook('app:rendered', () => {
      nuxtApp.payload[key] = get()
    })
  }

  if (import.meta.client) {
    const fromSsr = nuxtApp.payload[key] as T | None
    nuxtApp.payload[key] = undefined
    if (fromSsr) set(fromSsr)
  }
}

const ssrpromise = createServerPromise()
const timerHydrate = useTimeout()

useHydrationComponent(
  'pagessearch',
  () => {
    return { context: data.context, inputFast: data.inputFast }
  },
  (inp) => {
    data.context = inp.context
    data.inputFast = inp.inputFast
    data.loading = false
    data.fetchState = 'stop'
    timerHydrate.set(() => (data.fetchState = 'other'), 200)
  }
)

// ---------------- SSR ----------------

let isFetching = false
const FILTER_KEY_START = 'filter-'
const RANGE_KEY_START = 'range-'

async function setUrl(args: { replace: boolean; catalog: gen.SearchCatalogQuery['catalogSeller'] }): Promise<void> {
  const input = args?.catalog?.input
  const addQuery: Record<string, string | string[]> = {}

  if (input?.query) addQuery.q = input.query
  // New is default so if empty is either way new
  if (input?.sort && input?.sort !== 'New') addQuery.sort = input.sort
  if (input?.sku?.length) addQuery.sku = input.sku?.slice()
  if (input?.limitTo?.length) addQuery.limit = input?.limitTo?.slice()
  if (input?.sellerCollection) addQuery.collection = input?.sellerCollection

  const page = input?.cursor?.number
  // page default is 1
  if (page && page !== 1) addQuery.page = page.toString()

  const catalogUiFiltersValues = args?.catalog?.catalogUiFiltersValues
  input?.filters?.forEach((i) => {
    if (i.min || i.max) {
      addQuery[RANGE_KEY_START + i.key] = [i.min?.toString() || '0', ...helpers.arrify(i.max?.toString())]
    }

    const multi = i.multi?.length && catalogUiFiltersValues?.length ? i.multi.filter((i) => !catalogUiFiltersValues.includes(i)) : i.multi

    if (multi?.length) addQuery[FILTER_KEY_START + i.key] = multi
  })

  const all = input?.catalogSlug ? [input?.catalogSlug] : input?.department ? ['d', input?.department] : []

  const options = props.options
  const link = myLink(helpers.toString(all.length ? options.routeNameWithParams : options.routeName), all.length ? { all } : undefined, addQuery)

  await (args.replace ? router.replace : router.push)(link)
}

function fixQuery(i: unknown) {
  const str = i?.toString?.()
  return typeof str === 'string' && !!str.length ? str : undefined
}

let fetchCatalogDataPrev: undefined | gen.InputCatalog
function isInputCatalogEqual(input: gen.InputCatalog) {
  return isEqualSimple(fetchCatalogDataPrev, input)
}

const varsStatic = useValueCache((): InputCatalogStatic => {
  const options = props.options
  const sellerId = options.sellerId?.() || undefined
  // const b2bValue = b2b.value
  return {
    isB2b: options.isB2b,
    seller: { sellerId },
    // visibility: b2bValue.visibility,
    // priceGroups: b2bValue.priceGroups,
  }
})

const fetchingEnanabled = computed(() => {
  const options = props.options
  const sellerId = options.sellerId?.()
  return typeof options.sellerId !== 'function' || !!sellerId
})

function fetchLoading(loading: None | SearchOptions) {
  const next = !!loading
  if (next !== data.loading) data.loading = next

  let loadingLoadMoreSkeletonsPage: typeof data.loadingLoadMoreSkeletonsPage
  if (loading?.loadMore) loadingLoadMoreSkeletonsPage = { nextPage: loading.loadMore.nextPage, size: 60 }
  data.loadingLoadMoreSkeletonsPage = loadingLoadMoreSkeletonsPage
}

async function saveResults(catalog: gen.SearchCatalogQuery['catalogSeller'] | None, options: SearchOptions) {
  try {
    if (data.logError) data.logError = undefined
    const full = toUi({ catalog, sellerId: varsStatic.value.seller.sellerId, options, prev: data.context })
    data.readDescription = false
    data.context = full.context
    if (!full.context?.pages?.find((i) => i.last)?.items?.length && !full.context.input?.query?.length) console.error('[catalog search empty]', full.context.input)
    if (!isEqualSimple(data.inputFast, full.inputFast)) data.inputFast = full.inputFast
    if (import.meta.browser) await helpers.sleep(10)
    await setUrl({ replace: options.replace === true, catalog })
    if (import.meta.browser) await helpers.sleep(25)
  } finally {
    fetchLoading(undefined)
  }
}

function fetchCatalogData(vars: ReturnType<typeof toInputCatalog>, input: InputCatalogDynamic, options: SearchOptions) {
  if (!fetchingEnanabled.value) {
    console.error('[catalog:fetchCatalogData-never]', { input, options })
    return
  }
  if (isInputCatalogEqual(vars)) fetchLoading(undefined)
  else {
    fetchCatalogDataPrev = vars
    fetchLoading(options)
    return apollo
      .query<gen.SearchCatalogQuery, gen.SearchCatalogQueryVariables>({ query: TypedDocumentString.getQueryDocument(SearchCatalog), variables: { input: vars } })
      .then((res) => ({ renderCatalog: () => saveResults(res.data?.catalogSeller, options) }))
      .catch((err) => {
        console.error('[catalog searach]', err)
        data.logError = 'FAILD_FETCH'
        fetchLoading(undefined)
        return null
      })
  }
}

async function fullSearch(type: 'delay' | 'immidiate', input: InputCatalogDynamic, options: SearchOptions): Promise<void> {
  const vars = toInputCatalog(input, varsStatic.value)
  if (type === 'immidiate') {
    await fetchCatalogData(vars, input, { replace: true })?.then((data) => data?.renderCatalog())
  } else {
    if (isInputCatalogEqual(vars)) return fetchLoading(undefined)
    fetchLoading(options)
    const [delayFetch, delayRender] = !options?.delay ? [0, 0] : isFetching ? [500, 10] : [300, 0]
    const { stop } = start()
    isFetching = true
    const renderPromise = stop(delayFetch + delayRender)
    if (await stop(delayFetch)) return
    const promise = fetchCatalogData(vars, input, options)
    if (await renderPromise) return
    const values = await promise
    if (await stop(0)) return
    await values?.renderCatalog()
    isFetching = false
  }
}

const prevRoute = usePrevRoute()
function getPrevSlug(): string | undefined {
  const from = prevRoute.value
  return from?.name?.toString().includes('p-slug') && from.params.slug ? helpers.unArray(from.params.slug) : undefined
}

function scrollToProduct(slug: string) {
  return import('nuxt-base/utils/utilsScrollTop').then((i) => i.scrollToElem(document.getElementById(`product:${slug}`), { behavior: 'smooth', offsetTop: 60, tolarance: 0 }))
}

useValueCache(
  (): gen.InputCatalog | undefined => {
    if (!fetchingEnanabled.value) return
    const { params, query } = route
    const parseUrl = () => {
      const [one, two] = 'all' in params ? helpers.arrify(params.all) : []
      if (one === 'd' && two) return { department: two }
      if (one === 'c' && two) return { collection: two }
      if (one?.length && !two?.length) return { collection: one }
    }
    const ress = parseUrl()

    const collection = ress?.collection
    const department = ress?.department

    const filters = helpers.arr(Object.entries(query), ([key, values]): gen.CatalogFiltersInput | None => {
      const arr = helpers.arr(helpers.arrify(values), (i) => fixQuery(i))
      if (!arr.length) return
      if (key.startsWith(FILTER_KEY_START)) {
        return { key: key.slice(FILTER_KEY_START.length), multi: arr }
      }
      if (key.startsWith(RANGE_KEY_START)) {
        const [min, max] = arr.map((i) => {
          const num = +i
          return helpers.isNumber(num) ? num : undefined
        })
        return { key: key.slice(RANGE_KEY_START.length), min, max }
      }
    })

    const p = helpers.unArray(query.page)
    const p2 = !p ? null : Number.parseInt(p)
    const limit = helpers.arr(helpers.arrify(query.limit), (i) => {
      const val = fixQuery(i)
      return val ? helpers.toEnum(CatalogSearchLimitTo, val) : undefined
    })

    return toInputCatalog(
      {
        limitTo: helpers.arrClean(limit),
        sellerCollection: fixQuery(helpers.unArray(query.collection)),
        //
        query: fixQuery(query?.q),
        sku: helpers.arrClean(helpers.arr(helpers.arrify(query.sku), (i) => fixQuery(i))),
        filters,
        cursor: { number: helpers.toPositive(helpers.isNumber(p2) ? p2 : undefined, 1), size: pageSize.value },
        sort: helpers.toEnum(EnumCatalogSort, fixQuery(helpers.unArray(query.sort))) || 'New',
        catalogSlug: fixQuery(collection || query?.slug),
        department: fixQuery(department || query?.d),
      },
      varsStatic.value
    )
  },
  async (input) => {
    try {
      if (!input) return
      if (data.fetchState === 'first') {
        data.fetchState = 'other'
        await fullSearch('immidiate', input, { replace: true })
      } else if (data.fetchState === 'other' && !isInputCatalogEqual(input) && !data.loading) {
        filterSet(undefined)
        await fullSearch('delay', input, { replace: true })
      }
    } finally {
      if (import.meta.client) {
        data.prevSlug = getPrevSlug()
        if (data.prevSlug) await scrollToProduct(data.prevSlug)
      }

      ssrpromise.resolve?.()
    }
  }
)

function inputSet(inp: Omit<InputCatalogUi, keyof InputCatalogStatic>, options = { delay: true }): void {
  const filters = clearnFilters(inp.filters)
  data.inputFast = { ...inp, filters, ...varsStatic.value }
  fullSearch(
    'delay',
    {
      ...inp,
      sellerCollection: inp.sellerCollection?.key,
      department: inp?.department?.key,
      limitTo: inp.limitTo?.map((i) => i.key),
      filters,
      cursor: { number: 1, size: pageSize.value },
    },
    options
  )
}

function inputAdd(inp: Parameters<typeof inputSet>[0]): void {
  return inputSet({ ...data.inputFast, ...inp })
}

const updaters: Updaters = {
  sellerCollectionSer(sellerCollection) {
    return inputAdd({ sellerCollection })
  },
  limitToSet(limitTo) {
    return inputAdd({ limitTo, catalogSlug: undefined })
  },
  clearAll() {
    return inputSet({ sort: data.context?.input?.sort })
  },
  removeQuery() {
    return inputAdd({ query: undefined, catalogSlug: undefined })
  },
  setDepartment(department) {
    return inputAdd({ catalogSlug: undefined, department: department?.key ? { key: department.key, name: department.name } : undefined })
  },
  setSku(sku) {
    return sku?.length ? inputSet({ sku }) : inputAdd({ sku: undefined })
  },
  setFilters(filtersArgs) {
    const oldArgs = data.inputFast
    const catalogUiFilters = data.context.catalog?.catalogUiFiltersValues
    const filters = filtersArgs
    // const filters = clearnFiltersArgs(filtersArgs || oldArgs?.filters)
    return inputSet({
      ...oldArgs,
      filters,
      catalogSlug: !filters?.length ? undefined : catalogUiFilters?.every((i) => filters.some((j) => j.key === i)) ? oldArgs?.catalogSlug : undefined,
    })
  },
  setSorting(sort) {
    return inputAdd({ sort })
  },
  async changePage(number): Promise<NotVoidPrimitives> {
    if (data.loading) return null

    const prev = data.context?.input
    if (!prev) {
      data.logError = 'UPDATE_FAILED'
      return null
    }
    const pages = data.context.pages

    if (!pages.some((i) => i.pageNumber === number)) {
      const nextPage = (pages.at(-1)?.pageNumber || 1) + 1
      await fullSearch('delay', { ...prev, cursor: { number, size: pageSize.value } }, { loadMore: !number || nextPage === number ? { nextPage } : undefined })
    }

    const slug = data.context.pages.find((i) => i.pageNumber === number)?.items?.find((i) => i.slug)?.slug
    if (slug) await scrollToProduct(slug)

    return null
  },
}

function filterSet(inp: FilterActive | None) {
  data.filters = { filter: inp, type: !inp?.type ? 'none' : data.filters.type }
}
function filterToggle(type: FiltersType) {
  data.filters = { ...data.filters, type: data.filters.type === type ? 'none' : type }
}

function filterCount(val: gen.CatalogFiltersInput | None) {
  const isNumIsOne = (i: number | None) => (helpers.isNumber(i) ? 1 : 0)
  return Array.isArray(val?.multi) ? val?.multi?.length || 0 : isNumIsOne(val?.min) + isNumIsOne(val?.max)
}

const locale = useLocale()

const ui = computed(() => {
  const ctx = data.context
  return {
    desk: data.context.catalog?.description || (locale.value === 'en' ? SEO_DESCRIPTION_EN : SEO_DESCRIPTION_LT),
    title: ctx.input?.query ? helpers.joinString(': ', t2('Search'), ctx.input?.query) : ctx.catalog?.title || t2('All Products'),
    error: ctx?.pages?.length ? undefined : ctx?.catalog?.input?.catalogSlug ? t2('No products in category') : t2('No products'),
  }
})

// const pageInited = useDelayInit(3000)
// const hasFilters = computed((): boolean => {
//   const i = data.inputFast
//   return !!i.limitTo?.length || !!i.department?.key || !!i.query?.length || !!i.sku?.length || !!i.filters?.length
// })

const provideContext = {
  varsStatic,
  departmentImage: (i: gen.ImageFragment | None) => (data.departmentImage = i),
  feedSize,
  filterCount,
  t2,
  filterSet,
  filterToggle,
  t3,
  updaters,
  filters: toRef(() => data.filters),
  context: toRef(() => data.context),
  inputFast: toRef(() => data.inputFast),
  sellerId: toRef(() => varsStatic.value.seller.sellerId || undefined),
  isLoading: toRef(() => data.loading),
}

export type ProvidedValue = typeof provideContext

provide(catalogSearchContextKey, provideContext)

const breadCums = useBreadcums(() => {
  return {
    before: props.options.breadcrums(),
    catalog: data.context.catalog?.breadcrums,
    lastSup: data.context.totalCount > 10000 ? '10000+' : data.context.totalCount,
  }
})

const uiMainImage = computed(() => data.departmentImage || data.context.catalog?.image)

const showB2bFeed = computed((): boolean => feedSize.value === 999)
const showFeed = computed((): boolean => feedSize.value === 1)
const feedClass = computed((): string | undefined => (!mobile.value ? `feed${feedSize.value}` : showB2bFeed.value ? 'feedB2B' : showFeed.value ? 'feed' : undefined))
const ringKey = computed((): string => `m${!mobile.value ? feedSize.value : showFeed.value ? 1 : 2}`)
const pageInfo = computed(() => data.context.catalog?.pageInfo)
const description = useTemplateRef('description')

const privateCatalog = useCatalogPrivate(() => data.context?.pages.flatMap((i) => i.items), 'private')

const pagesUi = computed((): Replace<ProductPage, { items: ProductListingBoxFragmentFull[] }>[] => {
  const pages = helpers.arr(data.context?.pages, (page) => ({ ...page, items: privateCatalog.toBoxes(page.items) }))
  const x = data.loadingLoadMoreSkeletonsPage
  if (!x) return pages
  return [...pages, { items: [], last: true, pageNumber: x.nextPage, renderSkeletons: x.size }]
})

useHead({
  title: () => {
    const p = data.context.catalog?.pageInfo?.currentPage || 1
    return p > 1 ? `${ui.value.title} - ${locale.value === 'en' ? 'Page' : 'Psl.'} ${p}` : ui.value.title
  },
})

if (import.meta.server) {
  useSeoSearch(() => ({
    context: data.context,
    title: ui.value.title,
    description: ui.value.desk,
    image: uiMainImage.value,
    breadcrumbs: { list: breadCums.value, hidden: undefined },
  }))
}
</script>

<template>
  <div :class="!options.isB2b && $style.buy">
    <div v-if="data.logError" :class="$style.err">
      <h3 class="_fs21 _pt4 _pb4 _ctr">{{ t2('Incorrect catalog search') }}</h3>
    </div>

    <template v-if="data.context">
      <div :class="[$style.pad, $clsClass('head')]">
        <BaseImage v-if="!mobile && uiMainImage" :class="$style.depImg" :img="uiMainImage" sizes="420px" ratio="4:5" />

        <BreadcrumsLine :list="breadCums" class="d_pt4 m_pt08 m_pl1 m_pb1" :class="[$style.bread, $clsClass('bread')]" :pagename="ui.title" nowrap />
        <h1 v-if="ui.title" :class="mobile && '_pl1'" class="_pr1 _hm80">{{ ui.title || ' ' }}</h1>

        <div class="_pb2 _hm100">
          <template v-if="ui.desk">
            <p ref="description" class="_short _fs1 m_pl1 m_pr04 _relative _bg0 _br9 m_pt1 _pr1 _pb1 _glass _hidden" :class="data.readDescription ? undefined : '_h80'">
              {{ ui.desk }}
              <LazyCatalogSearchLoadMore v-if="init3" v-model="data.readDescription" :description="ui.desk" :elem="() => description" />
            </p>
          </template>
          <LazyDocumentUi v-if="data.context.documentId" class="_pb4" :document-id="data.context.documentId" />
        </div>

        <div class="_hm250" :class="$clsClass('nav')">
          <LazyCatalogSearchFiltersLazy v-if="init1" />
        </div>
      </div>

      <div class="_hm64" :class="$clsClass('select')">
        <LazyCatalogSelected v-if="init1" :class="$style.pad" />
      </div>

      <template v-if="pagesUi.length">
        <div :class="[$style.products, data.loading && !data.context.isEmpty && 'loading _noselect', feedClass, $clsClass('search-feed')]">
          <template v-if="data.context.isEmpty">
            <CatalogSearchLoading v-if="data.loading" :size="60" />
          </template>
          <template v-else>
            <template v-for="page in pagesUi" :key="page.pageNumber">
              <CatalogSearchLoading v-if="page.renderSkeletons" :size="page.renderSkeletons" />

              <Suspense v-if="showB2bFeed">
                <LazyCatalogSearchB2bPage />
                <template #fallback><UiLoadingBox :repeat="1" height="_h80" width="_w100p" /></template>
              </Suspense>

              <Suspense v-else-if="!mobile && showFeed">
                <LazyProductBoxesDesktopFeed :products="page.items" :to-id="(i) => `product:${i.slug}`" :route-to="(i) => options.toProductRoute?.(i) ?? undefined" />
                <template #fallback><UiLoadingBox :repeat="4" height="_h80" width="_w100p" /></template>
              </Suspense>

              <Suspense v-else-if="mobile && showFeed">
                <LazyProductBoxesMobileFeed :products="page.items" :to-id="(i) => `product:${i.slug}`" :route-to="(i) => options.toProductRoute?.(i) ?? undefined" />
                <template #fallback><UiLoadingBox :repeat="4" height="_h80" width="_w100p" /></template>
              </Suspense>

              <ProductBoxList v-else :products="page.items" :to-id="(i) => `product:${i.slug}`" :mobile :route-to="(i) => options.toProductRoute?.(i) ?? undefined">
                <template #box="pass"><LazyProductListBottom v-if="init3" v-bind="pass" /></template>
              </ProductBoxList>

              <LazyVisiblePaginationView
                v-if="page.items.length || page.renderSkeletons"
                hydrate-on-visible
                :border="!page.last"
                :input="page.last ? pageInfo : pageInfo ? { ...pageInfo, currentPage: page.pageNumber } : undefined"
                :change="(i) => updaters.changePage(i)"
                :class="[$style.pag, $clsClass('pagination')]"
              />
            </template>
          </template>
        </div>
      </template>

      <div v-else-if="ui.error" class="_fs17 _txt_ctr _mt4 _mb4" :class="$style.pad">{{ ui.error }}</div>
    </template>

    <LazyCatalogSearchLastActiveRing v-if="init3" :key="ringKey" :slug="data.prevSlug" />

    <LazyAdsViewItems v-if="initAds" v-memo="[ui.title]" :items="ui.title && pagesUi.at(-1)?.items?.length ? { items: pagesUi.at(-1)?.items!, title: ui.title! } : undefined" />
  </div>
</template>

<style module>
/* TODO-CSS */
.buy {
  composes: global(_pt-nav);
  composes: global(_pb2);
  /* --pad0: 30px; */
  --pad0: 16px;
  --pad1: 10px;
  --pad: var(--pad0);
}
:global(.m) .buy {
  --pad0: 0;
}
.pad {
  padding-left: var(--pad);
  padding-right: var(--pad);
}

.pag {
  composes: pad;
  composes: global(_h120);
  composes: global(d_pt4);
  composes: global(m_pt3);
  composes: global(m_pl1);
  composes: global(m_pr1);
  grid-column: span var(--span);
  --pad: calc(var(--pad0) - var(--pad1, 0));
}

:global(.m) .pad .bread {
  gap: 4px 0;
}
.err {
  composes: pad;
  min-height: 60vh;
  composes: global(_txt_ctr);
}

.products {
  composes: pad;
  composes: global(_grid);
  composes: global(_relative);

  transition: opacity 300ms cubic-bezier(0, 0.92, 0.41, 0.33);
  grid-template-columns: repeat(var(--span), minmax(0, 1fr));
  gap: 0 2px;
  --span: 4;
  --pad: var(--pad1);
}

:global(.d) .products:global(.feed6) {
  --pad: var(--pad1);
  --span: 6;
}
:global(.d) .products:global(.feed10) {
  --pad: var(--pad1);
  --span: 10;
}
:global(.d) .products:global(.feed1) {
  --pad: var(--pad1);
  --span: 1;
}
:global(.d) .products:global(.feed999) {
  --pad: 0;
  --pad1: 2;
  --span: 1;
}
:global(.m) .products {
  --span: 2;
  gap: 14px 4px;
  padding: 0;
}
:global(.m) .products:global(.feedB2B) {
  --span: 1;
  gap: 14px 4px;
  padding: 0;
}
:global(.m) .products:global(.feed) {
  min-height: 90vw;
  --span: 1;
}
.products:global(.loading) {
  opacity: 0.7;
}
.depImg {
  composes: global(_absolute);
  composes: global(_br14);
  composes: global(_noselect);

  right: 4px;
  top: 4px;
  width: 396px;
  height: 526px;
  z-index: 0;
}
.depImg img {
  object-position: 50% 50%;
}
</style>
